import React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type MenuItemOption = {
  key: string;
  label: string;
  icon?: IconDefinition;
  iconBoxClassName?: string;
  iconClassName?: string;
  labelClassName?: string;
  isSelected?: boolean;
  onSelect?: () => void;
  showDividerBefore?: boolean;
};

type Props = {
  label: string;
  items: MenuItemOption[];
  isLoading?: boolean;
  showChevronUpWhenOpen?: boolean;
  showCheckIcon?: boolean;
  buttonClassName?: string;
  labelClassName?: string;
  chevronClassName?: string;
  spinnerClassName?: string;
  menuClassName?: string;
  itemButtonClassName?: string;
  itemLeftClassName?: string;
  itemLabelClassName?: string;
  itemCheckClassName?: string;
  dividerClassName?: string;
};

const MenuComponent: React.FC<Props> = ({
  label,
  items,
  isLoading = false,
  showChevronUpWhenOpen = false,
  showCheckIcon = true,
  buttonClassName,
  labelClassName,
  chevronClassName,
  spinnerClassName,
  menuClassName,
  itemButtonClassName,
  itemLeftClassName,
  itemLabelClassName,
  itemCheckClassName,
  dividerClassName,
}) => {
  return (
    <Menu>
      {({ open }) => (
        <>
          <MenuButton
            className={buttonClassName}
            disabled={isLoading}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <p className={labelClassName}>{label}</p>

            {isLoading ? (
              <span className={spinnerClassName} aria-hidden="true" />
            ) : (
              <FontAwesomeIcon
                className={chevronClassName}
                icon={
                  open && showChevronUpWhenOpen ? faChevronUp : faChevronDown
                }
                aria-hidden="true"
              />
            )}
          </MenuButton>

          <MenuItems anchor="bottom end" transition className={menuClassName}>
            {items.map((item) => (
              <React.Fragment key={item.key}>
                {item.showDividerBefore && <div className={dividerClassName} />}
                <MenuItem>
                  <button
                    type="button"
                    className={itemButtonClassName}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isLoading) return;
                      item.onSelect?.();
                    }}
                  >
                    <div className={itemLeftClassName}>
                      {item.icon && (
                        <div
                          className={item.iconBoxClassName}
                          aria-hidden="true"
                        >
                          <FontAwesomeIcon
                            icon={item.icon}
                            className={item.iconClassName}
                          />
                        </div>
                      )}
                      <p className={item.labelClassName ?? itemLabelClassName}>
                        {item.label}
                      </p>
                    </div>

                    {showCheckIcon && item.isSelected && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className={itemCheckClassName}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </MenuItem>
              </React.Fragment>
            ))}
          </MenuItems>
        </>
      )}
    </Menu>
  );
};

export default MenuComponent;
